import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankOfferSummary from './BedbankOfferSummary'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import { connect } from 'react-redux'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import ShareModal from 'components/Common/Modals/ShareModal'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import {
  getOfferExtra,
  OFFER_VIEWS_24_HOURS_THRESHOLD,
} from 'selectors/offerExtraSelector'
import { fetchOfferExtraById } from 'actions/OfferActions'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { DAYS_TO_BOOK } from 'constants/bedbank'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { OptimizelyClientSideExperiments, OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import BedbankPropertyDetails from './BedbankPropertyDetails'
import BedbankPropertyDetailsExperimentVariant from './BedbankPropertyDetailsExperimentVariant'
import PropertyMapV2 from 'components/Common/PropertyMap/PropertyMapV2'

const RootContainer = styled(LayoutContainer)`
  padding-top: ${rem(32)};
  padding-bottom: ${rem(56)};
`

const SummaryArea = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: relative;
    align-self: stretch;
    display: block;
  }
`

const StickySummaryContent = styled.div`
  position: sticky;
  top: 0;
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${(props) => props.theme.palette.messaging.fast.main};
  background-color: ${(props) => props.theme.palette.messaging.fast.light};
`

interface Props {
  customerPortalURL: string;
  offer: App.BedbankOffer;
  regionCode: string;
  pathname: string;
  memberId: string;
  duration: number;
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  onViewPackageOptions?: () => void;
  rooms: Array<App.Occupants>;
  loadingPrices?: boolean;
  flightPrice?: number;
  flightsEnabled: boolean;
  offerExtra: App.OfferExtra;
}

function BedbankOfferIntro(props: Props) {
  const {
    checkinDate,
    checkoutDate,
    customerPortalURL,
    memberId,
    offer,
    onViewPackageOptions,
    pathname,
    rooms,
    regionCode,
    loadingPrices,
    flightsEnabled,
    flightPrice,
    offerExtra,
    duration,
  } = props

  const { heights } = useContext(HeaderHeightContext)
  const offerPageTruncateText = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.offerPageTruncateText)
  const offerPageMapInSummary = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageMapInSummary)

  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')

  const shareConfig = useMemo(
    () =>
      getShareModalConfig({
        customerPortalURL,
        image: offer.images[0],
        memberId,
        offerId: offer.id,
        offerLocation: location,
        pathname,
        region: regionCode,
        type: 'bedbankOffer',
        utmCampaign: 'webshare',
      }),
    [
      customerPortalURL,
      location,
      memberId,
      offer.id,
      offer.images,
      pathname,
      regionCode,
    ],
  )

  const urgencyToday = useMemo(() => {
    if (offerExtra) {
      const views =
        offerExtra.viewsInLast24Hours > 0 ?
          offerExtra.viewsInLast24Hours :
          offerExtra.viewsToday
      return {
        isActive: views >= OFFER_VIEWS_24_HOURS_THRESHOLD,
        captionText: `${views} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
    return { isActive: false }
  }, [offerExtra])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  const hasPromotions = offer.promotions.length > 0

  const bedBankOfferPromotionEndDate = offer.promotions[0]?.periodSchedule.to

  return (
    <RootContainer>
      <Group direction="vertical" gap={16}>
        <Group
          direction="vertical"
          tabletDirection="horizontal-reverse"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={4}
        >
          <Group
            direction="horizontal"
            gap={8}
            verticalAlign="center"
            horizontalAlign="end"
          >
            <TripGuard>
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </TripGuard>

            <TripGuard inverse>
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton outdent="right" />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </TripGuard>
          </Group>

          <LabelGroup>
            {hasPromotions && (
              <ProductTypeLabel productType="limited_time_special" />
            )}

            {hasPromotions && bedBankOfferPromotionEndDate && (
              <OfferUrgencyCountdownLabel
                endDate={bedBankOfferPromotionEndDate}
                daysToBook={DAYS_TO_BOOK}
              />
            )}

            {regionCode !== 'IN' && (
              <PromotionLabel variant="default" icon={<LineUserIcon />}>
                24-7 support
              </PromotionLabel>
            )}
          </LabelGroup>
        </Group>
        {urgencyToday.isActive && (
          <CSSBreakpoint max="mobile">
            <OfferViews>
              <CaptionTextBlock
                  variant="medium"
                  weight="bold"
                  startIcon={<urgencyToday.Icon />}
                >
                {urgencyToday.captionText}
              </CaptionTextBlock>
            </OfferViews>
          </CSSBreakpoint>
        )}
        <Group direction="horizontal" gap={48}>
          {offerPageTruncateText &&
            <BedbankPropertyDetailsExperimentVariant
              offer={offer}
              location={location}
              hasPromotions={hasPromotions}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              duration={duration}
              rooms={rooms}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
            />
          }
          {!offerPageTruncateText &&
            <BedbankPropertyDetails
              offer={offer}
              location={location}
              hasPromotions={hasPromotions}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              duration={duration}
              rooms={rooms}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
            />
          }
          <SummaryArea>
            <StickySummaryContent
              style={{
                top: rem(heights.bedbankOfferHeader + 16),
              }}
            >
              {urgencyToday.isActive && (
                <OfferViews>
                  <CaptionTextBlock
                    variant="medium"
                    weight="bold"
                    startIcon={<urgencyToday.Icon />}
                  >
                    {urgencyToday.captionText}
                  </CaptionTextBlock>
                </OfferViews>
              )}
              <BedbankOfferSummary
                loadingPrices={loadingPrices}
                onViewPackageOptions={onViewPackageOptions}
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                duration={duration}
                offer={offer}
                rooms={rooms}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
              />
              {offerPageMapInSummary && <CSSBreakpoint min="tablet">
                <PropertyMapV2 offer={offer}/>
              </CSSBreakpoint>}
            </StickySummaryContent>
          </SummaryArea>
        </Group>
      </Group>
    </RootContainer>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.config.customerPortalURL,
    memberId: state.auth.account.memberId,
    loadingPrices: !!state.offer.offerRatesLoading[ownProps.offer.id],
    offerExtra: getOfferExtra(state, ownProps.offer?.id),
  }
}

export default connect(mapStateToProps)(BedbankOfferIntro)
